import type { FC, MouseEvent, ReactNode } from 'react';
import { Children, isValidElement, useEffect, useMemo, useRef, useState } from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
// eslint-disable-next-line no-restricted-imports
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/prism';
// eslint-disable-next-line no-restricted-imports
import { atomDark as darkTheme, coy as lightTheme } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { toSession } from '@gonfalon/navigator/navigators';
import remarkGfm from 'remark-gfm';

import { vars } from '../../vars';
import { ArtifactChip } from '../Artifact';
import { getArtifactTypeLabel } from '../Artifact/artifactDisplay';

import { useArtifactLookup } from './ArtifactLookupContext';
import { parseTaskIndex, parseTaskLabel, rehypeTaskListIndices } from './rehypeTaskListIndices';
import type { VegaExternalSourceMatch } from './sourceUrlUtils';
import { matchVegaExternalSourceUrl } from './sourceUrlUtils';

import styles from './MarkdownRenderer.module.css';

const rehypePlugins = [rehypeTaskListIndices];

// Match <session-id>...</session-id> for linkification (session IDs are alphanumeric)
const SESSION_ID_TAG_REGEX = /<session-id>([^<]+)<\/session-id>/g;
const SESSION_LINK_PROTOCOL = 'session://';

// Match :::artifact{type=X,external_id=Y} tokens emitted by Vega. external_id is
// expected URL-encoded so it can contain `/`, `:`, and `=` without breaking the regex.
const ARTIFACT_TOKEN_REGEX = /:::artifact\{type=([a-z_]+),external_id=([^}]+)\}/g;
const VEGA_ARTIFACT_LINK_PROTOCOL = 'vega-artifact://';

// Strip :::hidden{...} single-line directives emitted by Vega skills.
// Single-line sentinels: :::hidden{feedback:slug}
const HIDDEN_LINE_REGEX = /:::hidden\{[^}\n]*\}[^\n]*\n?/g;

const stripHiddenDirectives = (text: string): string => text.replace(HIDDEN_LINE_REGEX, '');

/**
 * Pre-process text to convert <session-id>xxx</session-id> tags into markdown links
 * that can be handled by the custom link component.
 */
const preprocessSessionIds = (text: string): string => {
  return text.replace(SESSION_ID_TAG_REGEX, (_, sessionId: string) => {
    const trimmed = sessionId.trim();
    return `[${trimmed}](${SESSION_LINK_PROTOCOL}${trimmed})`;
  });
};

/**
 * Pre-process :::artifact{} tokens into markdown links of the form
 *   [Type label: externalId](vega-artifact://<type>/<encoded externalId>)
 * The custom `a:` component swaps the anchor for an ArtifactChip.
 */
const preprocessArtifactTokens = (text: string): string => {
  return text.replace(ARTIFACT_TOKEN_REGEX, (_, type: string, encodedExternalId: string) => {
    let decoded: string;
    try {
      decoded = decodeURIComponent(encodedExternalId);
    } catch {
      decoded = encodedExternalId;
    }
    const label = `${getArtifactTypeLabel(type)}: ${decoded}`;
    const safeLabel = label.replace(/\]/g, '\\]');
    return `[${safeLabel}](${VEGA_ARTIFACT_LINK_PROTOCOL}${type}/${encodedExternalId})`;
  });
};

interface ParsedArtifactHref {
  type: string;
  externalId: string;
}

const parseArtifactHref = (href: string): ParsedArtifactHref | null => {
  if (!href.startsWith(VEGA_ARTIFACT_LINK_PROTOCOL)) {
    return null;
  }
  const rest = href.slice(VEGA_ARTIFACT_LINK_PROTOCOL.length);
  const slash = rest.indexOf('/');
  if (slash <= 0 || slash === rest.length - 1) {
    return null;
  }
  const type = rest.slice(0, slash);
  const encoded = rest.slice(slash + 1);
  try {
    return { type, externalId: decodeURIComponent(encoded) };
  } catch {
    return { type, externalId: encoded };
  }
};

export type RenderArtifactTokenArgs = {
  projectKey: string;
  type: string;
  externalId: string;
};

export interface MarkdownRendererProps {
  text: string;
  projectKey: string;
  onExternalSourceClick?: (match: VegaExternalSourceMatch) => void;
  // When true, :::artifact{} tokens render as ArtifactChips (hydrated from the surrounding
  // ArtifactLookupProvider if present, otherwise via renderArtifactToken). When false
  // (default), tokens render as plain text. Gated by the consuming surface — typically
  // vegaArtifactGraph().
  artifactsEnabled?: boolean;
  // Optional consumer-provided renderer for artifact tokens that aren't in the lookup
  // context. Receives the parsed type and externalId; returns a ReactNode (typically a
  // chip wired to a lazy-fetch hook). When omitted, unresolved tokens render as a
  // placeholder ArtifactChip. Lives at the consumer because lazy-fetch requires a
  // dependency (observability-graphql) that this UI package deliberately doesn't pull in.
  renderArtifactToken?: (args: RenderArtifactTokenArgs) => ReactNode;
  onTaskAction?: (params: { action: 'toggle'; index: number; label: string } | { action: 'reset' }) => void;
  // Optional policy hook for generic external links in the rendered markdown. When provided, any
  // link whose href fails this predicate is rendered as inert text (no anchor) instead of a
  // clickable link. Consumers rendering attacker-influenceable content (e.g. agent responses)
  // pass a host/scheme allow-list here so the content can't link a user to an arbitrary site.
  // When omitted, all links render normally (react-markdown still strips dangerous schemes).
  isExternalUrlAllowed?: (href: string | null | undefined) => boolean;
}

// Simple markdown renderer component using react-markdown with GFM support
export const MarkdownRenderer: FC<MarkdownRendererProps> = ({
  text,
  projectKey,
  onExternalSourceClick,
  artifactsEnabled = false,
  renderArtifactToken,
  onTaskAction,
  isExternalUrlAllowed,
}) => {
  const [highlightTheme, setHighlightTheme] = useState<'light' | 'dark'>(() => {
    const initial = document.querySelector('html')?.dataset.theme;
    return initial === 'dark' ? 'dark' : 'light';
  });
  const [checkedTasks, setCheckedTasks] = useState<Record<number, boolean>>({});
  // Refs so the reset effect can notify the consumer without making `onTaskAction`
  // a dep — when the parent passes an inline callback, its reference changes on every
  // render, and listing it in the deps would wipe user selections on every parent
  // re-render even though `text` hasn't changed.
  const onTaskActionRef = useRef(onTaskAction);
  onTaskActionRef.current = onTaskAction;

  useEffect(() => {
    setCheckedTasks({});
    onTaskActionRef.current?.({ action: 'reset' });
  }, [text]);

  useEffect(() => {
    const detectTheme = () => {
      const htmlTheme = document.querySelector('html')?.dataset.theme;
      setHighlightTheme(htmlTheme === 'dark' ? 'dark' : 'light');
    };

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          detectTheme();
        }
      }
    });

    const htmlElement = document.querySelector('html');
    if (htmlElement) {
      observer.observe(htmlElement, { attributes: true, attributeFilter: ['data-theme'] });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const artifactLookup = useArtifactLookup();
  const processedText = artifactsEnabled
    ? preprocessArtifactTokens(preprocessSessionIds(stripHiddenDirectives(text)))
    : preprocessSessionIds(stripHiddenDirectives(text));

  const components = useMemo<Components>(() => {
    return {
      // Table: wrap in a scrollable container for narrow panels
      table: ({ children, ...props }) => {
        const { className, ...rest } = props;

        return (
          <div className={styles.tableWrapper}>
            <div className={styles.tableScroller}>
              <table className={[styles.table, className].filter(Boolean).join(' ')} {...rest}>
                {children}
              </table>
            </div>
          </div>
        );
      },
      thead: ({ children, ...props }) => {
        const { className, ...rest } = props;

        return (
          <thead className={[styles.tableHead, className].filter(Boolean).join(' ')} {...rest}>
            {children}
          </thead>
        );
      },
      th: ({ children, ...props }) => {
        const { className, ...rest } = props;

        return (
          <th className={[styles.tableHeaderCell, className].filter(Boolean).join(' ')} {...rest}>
            {children}
          </th>
        );
      },
      td: ({ children, ...props }) => {
        const { className, ...rest } = props;

        return (
          <td className={[styles.tableCell, className].filter(Boolean).join(' ')} {...rest}>
            {children}
          </td>
        );
      },
      // Block code wrapper — pass through when language-highlighted (SyntaxHighlighter
      // renders its own <pre>), otherwise provide the styled block container.
      pre: ({ children }) => {
        const childArray = Children.toArray(children);
        const hasLanguage = childArray.some((child) => {
          if (!isValidElement(child)) {
            return false;
          }
          const { className } = child.props as { className?: string };
          return typeof className === 'string' && /language-\w+/.test(className);
        });

        if (hasLanguage) {
          return <>{children}</>;
        }

        return (
          <div className={styles.preBlock}>
            <pre>{children}</pre>
          </div>
        );
      },
      // Code blocks with syntax highlighting; inline code styled via CSS module
      code: ({ className, children, ...props }) => {
        const match = className?.match(/language-(\w+)/);
        const codeString = String(children).replace(/\n$/, '');

        if (match) {
          return (
            <div className={styles.codeBlock}>
              <SyntaxHighlighter
                language={match[1]}
                style={highlightTheme === 'dark' ? darkTheme : lightTheme}
                customStyle={{
                  margin: 0,
                  padding: vars.space['12'],
                  fontSize: 13,
                  borderRadius: vars.borderRadius['6'],
                }}
                wrapLongLines
              >
                {codeString}
              </SyntaxHighlighter>
            </div>
          );
        }

        // Inline code and plain block code — styled via .renderer code / .renderer pre code in CSS module
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
      // Links — handle session:// protocol for session IDs and external source URLs
      a: ({ href, children, ...props }) => {
        if (artifactsEnabled && href?.startsWith(VEGA_ARTIFACT_LINK_PROTOCOL)) {
          const parsed = parseArtifactHref(href);
          if (parsed) {
            const resolvedProjectKey = artifactLookup?.projectKey ?? projectKey;
            const entry = artifactLookup?.lookup(parsed.type, parsed.externalId);
            if (entry) {
              return (
                <ArtifactChip
                  projectKey={resolvedProjectKey}
                  type={parsed.type}
                  externalId={parsed.externalId}
                  title={entry.title}
                  status={entry.status}
                  url={entry.url}
                  variant="inline"
                />
              );
            }
            if (renderArtifactToken) {
              return (
                <>
                  {renderArtifactToken({
                    projectKey: resolvedProjectKey,
                    type: parsed.type,
                    externalId: parsed.externalId,
                  })}
                </>
              );
            }
            return (
              <ArtifactChip
                projectKey={resolvedProjectKey}
                type={parsed.type}
                externalId={parsed.externalId}
                variant="inline"
                placeholder
              />
            );
          }
        }

        if (href?.startsWith(SESSION_LINK_PROTOCOL)) {
          const sessionId = href.slice(SESSION_LINK_PROTOCOL.length);
          const sessionUrl = toSession({ projectKey, sessionId });
          return (
            <a
              href={sessionUrl.pathname}
              className={styles.sessionLink}
              title="Open session"
              target="_blank"
              rel="noopener noreferrer"
              {...props}
            >
              {children}
            </a>
          );
        }

        const externalSource = matchVegaExternalSourceUrl(href);
        if (externalSource && onExternalSourceClick) {
          const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            onExternalSourceClick(externalSource);
          };
          return (
            <a
              href={externalSource.url}
              className={styles.sourceLink}
              onClick={handleClick}
              title="Continue this conversation in Vega"
              {...props}
            >
              {children}
            </a>
          );
        }

        // Generic external link. When the consumer supplies an allow-list policy, render
        // disallowed destinations as inert text so attacker-influenceable content can't link the
        // user to an arbitrary site. (react-markdown's urlTransform has already neutralized
        // dangerous schemes like javascript:, so this guards the host/destination.)
        if (isExternalUrlAllowed && !isExternalUrlAllowed(href)) {
          return (
            <span className={styles.blockedLink} title="Link to an untrusted destination was blocked">
              {children}
            </span>
          );
        }

        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
      // Task list checkboxes — index from rehypeTaskListIndices (parse phase, not render)
      input: ({ type, checked: defaultChecked, disabled: _disabled, node, ...rest }) => {
        if (type !== 'checkbox') {
          return <input type={type} {...rest} />;
        }
        const index = parseTaskIndex(node);
        if (index < 0) {
          return <input {...rest} type="checkbox" disabled className={styles.taskListInput} />;
        }
        const label = parseTaskLabel(node);
        const isChecked = !!(checkedTasks[index] ?? defaultChecked ?? false);
        return (
          <input
            {...rest}
            type="checkbox"
            checked={isChecked}
            className={styles.taskListInput}
            disabled={!onTaskAction}
            onChange={(event) => {
              const next = event.target.checked;
              setCheckedTasks((prev) => ({ ...prev, [index]: next }));
              onTaskAction?.({ action: 'toggle', index, label });
            }}
          />
        );
      },
    };
  }, [
    highlightTheme,
    projectKey,
    onExternalSourceClick,
    artifactsEnabled,
    artifactLookup,
    renderArtifactToken,
    onTaskAction,
    checkedTasks,
    isExternalUrlAllowed,
  ]);

  return (
    <div className={styles.renderer}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={rehypePlugins}
        components={components}
        // Preserve our custom protocols (session:// for session links, vega-artifact:// for
        // artifact chips). react-markdown's default urlTransform strips anything outside
        // http/https/mailto/tel, which would zero out the href before the custom `a:`
        // renderer can see it.
        urlTransform={(value) => {
          if (value.startsWith(SESSION_LINK_PROTOCOL) || value.startsWith(VEGA_ARTIFACT_LINK_PROTOCOL)) {
            return value;
          }
          return defaultUrlTransform(value);
        }}
      >
        {processedText}
      </ReactMarkdown>
    </div>
  );
};
